import { computed, inject, Injectable, signal } from '@angular/core';

import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';

import { delay, map, of, pipe, switchMap, tap } from 'rxjs';

import {
  ListOrganizationsParams,
  ListOrganizationTypesParams,
} from '@erm/cluster';

import {
  OrganizationSignalState,
  OrganizationWithIrbAffiliation,
} from '../../types';
import {
  ORGANIZATION_DEFAULT_SIGNAL_STATE,
  setOrganizationPageError,
  setOrgLoadingState,
} from '../../constants';
import { OrganizationsV2Service } from '../../services';
import { createPendingLookup } from '../../utils';

const ORGANIZATION_SIGNAL_STATE = signalState<OrganizationSignalState>({
  ...ORGANIZATION_DEFAULT_SIGNAL_STATE,
});

const ORGANIZATION_LIST_PAGE_SIZE = 25;

@Injectable()
export class OrganizationsListSignalState {
  private readonly _orgSignalState = ORGANIZATION_SIGNAL_STATE;
  private readonly _organizationService = inject(OrganizationsV2Service);
  private readonly _organizationNameById = signal<
    Record<string, string | null>
  >({});
  private readonly _organizationIrbAffiliatedById = signal<
    Record<string, boolean | null>
  >({});
  /** Base list params for the current type-ahead query (page is refreshed per request). */
  private readonly _organizationListSearchParams =
    signal<ListOrganizationsParams | null>(null);
  private readonly _organizationsListAppending = signal(false);

  readonly $orgList = computed(() => {
    const _orgs = this._orgSignalState.organizations().data?.results;
    if (!_orgs) {
      return [];
    }
    return _orgs.map((org) => ({
      ...org,
      primary_address: org.addresses?.find((a) => a.is_primary) ?? null,
    }));
  });

  readonly $selectorOrganizations = computed(() =>
    this.$orgList().map((organization) => ({
      ...organization,
      id: organization.organization_key,
    })),
  );

  readonly $orgTypes = computed(() => {
    const _orgTypes = this._orgSignalState.organizationTypes().data?.results;
    if (!_orgTypes) {
      return [];
    }
    return _orgTypes;
  });

  readonly $loading = computed(() => this._orgSignalState.loading());
  readonly $error = computed(() => this._orgSignalState.error());

  /** Inline message for organization-selector `errorText` when list fetch fails. */
  readonly $organizationsListErrorText = computed(() => {
    const errorState = this._orgSignalState.error();

    if (errorState?.type !== 'OrganizationsList' || !errorState?.error) {
      return '';
    }

    const msg = errorState.error.message?.trim();
    return msg ? msg : 'Something went wrong while loading organizations.';
  });

  readonly $organizationNameById = computed(() => this._organizationNameById());
  readonly $organizationIrbAffiliatedById = computed(() =>
    this._organizationIrbAffiliatedById(),
  );

  fetchOrgTypes = rxMethod<{
    payload: ListOrganizationTypesParams | null;
  }>(
    pipe(
      tap(() =>
        patchState(this._orgSignalState, {
          loading: setOrgLoadingState('OrganizationTypes', true),
          error: null,
        }),
      ),
      switchMap(({ payload }) =>
        this._organizationService.listOrganizationTypes(payload ?? {}).pipe(
          tapResponse({
            next: (response) =>
              patchState(this._orgSignalState, {
                organizationTypes: {
                  data: {
                    results: response.data?.results || [],
                    size: response.data?.size ?? 0,
                    page: response.data?.page ?? 0,
                  },
                },
                loading: setOrgLoadingState('OrganizationTypes', false),
              }),
            error: (error: Error) =>
              patchState(this._orgSignalState, {
                error: setOrganizationPageError('OrganizationTypes', error),
                loading: setOrgLoadingState('OrganizationTypes', false),
              }),
          }),
        ),
      ),
    ),
  );

  fetchOrganizations = rxMethod<{
    params: ListOrganizationsParams | null;
  }>(
    pipe(
      tap(() => {
        patchState(this._orgSignalState, {
          loading: setOrgLoadingState('OrganizationsList', true),
          error: null,
        });
      }),
      delay(500), // Ensure loading state is set before the API call
      switchMap(({ params }) =>
        this._organizationService.listOrganizations(params ?? {}).pipe(
          tapResponse({
            next: (response) => {
              patchState(this._orgSignalState, {
                organizations: {
                  data: {
                    results: response.data?.results ?? [],
                    size: response.data?.size ?? 0,
                    page: response.data?.page ?? 0,
                  },
                },
                loading: setOrgLoadingState('OrganizationsList', false),
              });
            },
            error: (error: Error) => {
              console.error(error);
              patchState(this._orgSignalState, {
                error: setOrganizationPageError('OrganizationsList', error),
                loading: setOrgLoadingState('OrganizationsList', false),
              });
            },
          }),
        ),
      ),
    ),
  );

  fetchOrganizationNamesByIds = rxMethod<{
    organizationIds: string[];
  }>(
    pipe(
      switchMap(({ organizationIds }) => {
        const uniqueIds = [...new Set(organizationIds.filter(Boolean))];
        if (uniqueIds.length === 0) {
          return of({
            organizationNameById: {},
            organizationIrbAffiliatedById: {},
          });
        }

        const pendingOrganizationNameById = createPendingLookup<string | null>(
          uniqueIds,
          null,
        );
        const pendingOrganizationIrbAffiliatedById = createPendingLookup<
          boolean | null
        >(uniqueIds, null);

        this._organizationNameById.set({
          ...this._organizationNameById(),
          ...pendingOrganizationNameById,
        });
        this._organizationIrbAffiliatedById.set({
          ...this._organizationIrbAffiliatedById(),
          ...pendingOrganizationIrbAffiliatedById,
        });

        return this._organizationService
          .listOrganizations({
            organization_keys: uniqueIds,
            page: 1,
            size: uniqueIds.length,
          })
          .pipe(
            map((response) => {
              const organizationNameById = { ...pendingOrganizationNameById };
              const organizationIrbAffiliatedById = {
                ...pendingOrganizationIrbAffiliatedById,
              };

              (response.data?.results ?? []).forEach((organization) => {
                const organizationWithIrbAffiliation =
                  organization as OrganizationWithIrbAffiliation;
                organizationNameById[
                  organizationWithIrbAffiliation.organization_key
                ] = organizationWithIrbAffiliation.organization_name ?? null;
                organizationIrbAffiliatedById[
                  organizationWithIrbAffiliation.organization_key
                ] = organizationWithIrbAffiliation.is_irb_affiliated ?? null;
              });

              return {
                organizationNameById,
                organizationIrbAffiliatedById,
              };
            }),
            tapResponse({
              next: ({
                organizationNameById,
                organizationIrbAffiliatedById,
              }) => {
                this._organizationNameById.set({
                  ...this._organizationNameById(),
                  ...organizationNameById,
                });
                this._organizationIrbAffiliatedById.set({
                  ...this._organizationIrbAffiliatedById(),
                  ...organizationIrbAffiliatedById,
                });
              },
              error: (error: Error) => {
                console.error(error);
                patchState(this._orgSignalState, {
                  error: setOrganizationPageError('OrganizationsList', error),
                });
              },
            }),
          );
      }),
    ),
  );

  /**
   * Fetch organizations based on a type-ahead query. This resets the current list and pagination.
   * The query is used to filter organizations by name using a case-insensitive like search.
   * */
  fetchByOrganizationQuery(query: string, status?: 'active' | 'inactive') {
    const params: ListOrganizationsParams = {
      organization_name__ilike: `%${query}%`,
      page: 1,
      size: ORGANIZATION_LIST_PAGE_SIZE,
      order_by: 'organization_name',
    };

    if (status) {
      params.status = status;
    }

    this._organizationListSearchParams.set(params);
    this.fetchOrganizations({ params });
  }
  /** Fetch the next page of organizations based on the current search params, if applicable. */
  fetchNextOrganizationPage() {
    const base = this._organizationListSearchParams();
    if (!base?.organization_name__ilike) {
      return;
    }

    const data = this._orgSignalState.organizations().data;
    // If there is no data or the last page returned fewer results than the page size, all data has been loaded.
    if (!data || data.results.length < ORGANIZATION_LIST_PAGE_SIZE) {
      // console.log('All organizations have been loaded, no further pages to fetch.');
      return;
    }

    if (this._organizationsListAppending()) {
      return;
    }

    const loading = this._orgSignalState.loading();
    if (loading?.type === 'OrganizationsList' && loading.isLoading) {
      return;
    }
    const currentPage = data.page ?? 1;
    this.fetchOrganizations({
      params: {
        ...base,
        page: currentPage + 1,
        size: ORGANIZATION_LIST_PAGE_SIZE,
      },
    });
  }

  /**
   * Clears the current list of organizations and resets related state. This is typically called when the type-ahead query is cleared or reset,
   * to ensure that stale data is not displayed while new data is being fetched based on updated search parameters.
   * */
  clearOrganizations() {
    this._organizationListSearchParams.set(null);
    this._organizationsListAppending.set(false);
    this._organizationNameById.set({});
    this._organizationIrbAffiliatedById.set({});
    patchState(this._orgSignalState, {
      organizations: {
        data: {
          results: [],
          size: 0,
          page: 0,
        },
      },
      loading: setOrgLoadingState('OrganizationsList', false),
      error: null,
    });
  }
}
